import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { notificationsKey } from '../components/NotificationCenter';
import { KIND_ICON } from '../components/notificationView';
import { newNotificationToasts, type ToastAudience } from '../utils/notificationToastModel';
import { PORTAL_APPROVAL_QUERY_KEYS } from '../utils/portalApprovalLive';

/** The toast stays a few seconds (D-145, D-285: up to 10 s for a toast with a button). */
const TOAST_MS = 8000;

/**
 * FIX-NOTIF N-5 - the ONE hook that turns new notifications into toasts, for the employee (Layout) and
 * the customer (portal). It replaces useTicketArrivalToasts, usePortalApprovalLive and the portal's
 * reply toasts. It rides the bell's own poll (same query key: no extra request); WHAT pops up is
 * decided in one place (utils/notificationToastModel.ts, Q5).
 */
export function useNotificationToasts(opts: {
  enabled: boolean;
  audience: ToastAudience;
  /** Portal with several organizations: the same narrowing as the bell, so they share one query. */
  organizationId?: string;
  /** Where "פתיחה" goes; default: navigate to the server's link. */
  onOpen?: (link: string | null) => void;
}): void {
  const { enabled, audience, organizationId, onOpen } = opts;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const known = useRef<Set<string>>(new Set());
  const initialized = useRef(false);
  const open = useRef(onOpen);
  open.current = onOpen;
  const { data, isSuccess } = useQuery({
    queryKey: notificationsKey(organizationId),
    queryFn: () => (organizationId ? api.getNotifications({ organization_id: organizationId }) : api.getNotifications()),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled,
  });

  // Another organization (or "all") is another feed: learn it afresh, so old notices do not pop up.
  const lastOrg = useRef(organizationId);
  if (lastOrg.current !== organizationId) {
    lastOrg.current = organizationId;
    known.current = new Set();
    initialized.current = false;
  }

  useEffect(() => {
    if (!enabled || !isSuccess) return;
    const arrivals = newNotificationToasts(data?.data ?? [], known.current, initialized.current, audience);
    initialized.current = true;
    if (arrivals.length === 0) return;
    // A new approval notice refreshes the portal's lists and counters at once (instead of their own 30 s poll).
    if (arrivals.some((a) => a.refreshesApprovals)) {
      for (const key of PORTAL_APPROVAL_QUERY_KEYS) queryClient.invalidateQueries({ queryKey: [...key] });
    }
    for (const a of arrivals.slice(0, 3)) {
      // "קריאה חדשה מ..." with no organization name: "מהלקוח" (the pre-N-5 fallback).
      const params = a.messageKey.startsWith('ticketArrival.') && !a.params.from ? { ...a.params, from: t('ticketArrival.customer') } : a.params;
      const base = t(a.messageKey, params);
      toast('info', a.note ? `${base}\n${a.note}` : base, {
        duration: TOAST_MS,
        icon: KIND_ICON[a.kind],
        avatar: a.avatar ?? undefined,
        action: {
          label: t('notifToast.open'),
          onClick: () => (open.current ? open.current(a.link) : (a.link ? navigate(a.link) : undefined)),
        },
      });
    }
  }, [data, isSuccess, enabled, audience, queryClient, navigate, t]);
}
